import React, { useState } from 'react';

import { useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import Modal from 'components/modal';
import { ProgressDots } from 'components/progress-dots';
import { ImageWithHeaderAndText } from 'components/widgets/image-with-header-and-text';
import { ClickEventComponentNames, EventTypes, logButtonClick } from 'state/amplitude';
import { useBrazeContext } from 'state/braze';
import { t } from 'state/intl/translations';
import { useOnboardingContext } from 'state/onboarding/onboarding';
import { getDesignToken, primitive } from 'styles/constants';
import { isIOS } from 'utils/environment';

import { ContentContainer, NavigationContainer, Stepper } from './styled';

export const OnboardingScreensModal = () => {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isLoadingPerm, setIsLoadingPerm] = useState<boolean>(false);
  const { isInAppMessageOpen } = useBrazeContext();
  const {
    triggerRule,
    onButtonPress,
    featureOnboardingScreens,
    isFirstAppLoadOnboardingComplete,
    currentScreenIndex,
    navigateBackward,
    isFirstScreen,
    isLastScreen,
    numberOfScreens,
  } = useOnboardingContext();
  const dimension = useWindowDimensions();
  const { bottom } = useSafeAreaInsets();
  const footerSafeAreaStyle = { paddingBottom: bottom };

  if (
    !numberOfScreens ||
    isFirstAppLoadOnboardingComplete ||
    !triggerRule ||
    !isOpen ||
    isInAppMessageOpen
  ) {
    return null;
  }

  const onPressCore = (url?: string) => {
    setIsLoadingPerm(true);
    onButtonPress(featureOnboardingScreens[currentScreenIndex], url, () => {
      setIsLoadingPerm(false);
      if (isLastScreen) {
        setIsOpen(false);
      }
    });
  };

  const onPressCta = (url?: string) => {
    logButtonClick({
      type: EventTypes.Other,
      attributes: {
        component: ClickEventComponentNames.BUTTON,
        name:
          'Onboarding ' +
          (featureOnboardingScreens[currentScreenIndex]?.buttonText || url || 'button'),
        identification: featureOnboardingScreens[currentScreenIndex]?.id,
      },
    });
    onPressCore(url);
  };

  const onPressSkipButton = () => {
    logButtonClick({
      type: EventTypes.Other,
      attributes: {
        component: ClickEventComponentNames.BUTTON,
        name: 'Onboarding Skip ' + featureOnboardingScreens[currentScreenIndex]?.buttonText,
        identification: featureOnboardingScreens[currentScreenIndex]?.id,
      },
    });
    onPressCore('/OS/continue');
    setIsLoadingPerm(false);
  };

  return (
    <Modal
      testID={'onboarding-modal'}
      allowsDismiss={false}
      isFullHeightContent
      height="100%"
      amplitudeEventData={{
        modalAppearanceEventMessage: 'Onboarding Triggered',
        modalName: 'App Tracking Modal',
      }}
      backgroundColor={primitive.$white}
      contentStyle={{
        borderRadius: 0,
        height: dimension.height,
        maxHeight: dimension.height,
        minHeight: dimension.height,
      }}
      body={
        <ContentContainer>
          <ImageWithHeaderAndText
            image={featureOnboardingScreens[currentScreenIndex]?.image ?? null}
            imageDescription={featureOnboardingScreens[currentScreenIndex]?.imageDescription ?? ''}
            textContent={featureOnboardingScreens[currentScreenIndex]?.textContent ?? []}
            buttonText={featureOnboardingScreens[currentScreenIndex]?.buttonText ?? ''}
            buttonUrl={featureOnboardingScreens[currentScreenIndex]?.buttonUrl ?? ''}
            secondaryButtonText={
              featureOnboardingScreens[currentScreenIndex]?.secondaryButtonText ?? ''
            }
            secondaryButtonUrl={
              featureOnboardingScreens[currentScreenIndex]?.secondaryButtonUrl ?? ''
            }
            onButtonPress={onPressCta}
            loading={isLoadingPerm}
            pictureStyles={{ height: dimension.height * 0.65 }}
          />
        </ContentContainer>
      }
      footer={
        <NavigationContainer mb={isIOS() ? '$6' : '$2'} style={footerSafeAreaStyle}>
          <ActionButton
            variant={ActionButtonVariants.TEXT_ONLY}
            size={ActionButtonSizes.SMALL}
            onPress={navigateBackward}
            disabled={isFirstScreen}
            {...(isFirstScreen && { _text: { color: 'transparent' } })}
          >
            {t('previous')}
          </ActionButton>
          {numberOfScreens > 1 && (
            <Stepper>
              <ProgressDots dotCount={numberOfScreens} activeDotIndex={currentScreenIndex} />
            </Stepper>
          )}
          <ActionButton
            skipLogEvent
            variant={ActionButtonVariants.TEXT_ONLY}
            size={ActionButtonSizes.SMALL}
            onPress={() => {
              onPressSkipButton();
            }}
            _text={{ color: getDesignToken('icon-button-secondary') }}
            alignSelf="flex-end"
          >
            {t('skip')}
          </ActionButton>
        </NavigationContainer>
      }
    />
  );
};
